/**
 * CrossTide Web — Main entry point.
 *
 * Bootstrap: load config, initialize UI, set up event listeners.
 */
// G16: self-hosted Inter variable font (replaces system fallback)
import "@fontsource-variable/inter";
import {
  loadConfig,
  saveConfig,
  addTicker,
  removeTicker,
  reorderWatchlist,
  updateWatchlistNames,
  updateWatchlistInstrumentTypes,
  hasStoredConfig,
} from "./core/config";
import { createCrossTabSync } from "./core/broadcast-channel";
import { createCrossTabShareSync } from "./core/cross-tab-share";
import { registerServiceWorker } from "./core/sw-register";
import { activateServiceWorkerUpdate, watchServiceWorkerUpdates } from "./core/sw-update";
import { createShortcutManager } from "./core/keyboard";
import {
  initRouter,
  isValidRouteName,
  navigateTo,
  navigateToPath,
  onRouteChange,
  getCurrentRouteInfo,
  type RouteName,
} from "./ui/router";
import { detectPreferredTheme, initTheme } from "./ui/theme";
import { getThemeOverride, initAutoThemeSync, setThemeOverride } from "./ui/auto-theme-sync";
import { initEnhancedContrast } from "./core/contrast-preference";
import { initLocale } from "./core/i18n";
import {
  renderWatchlist as renderWatchlistCore,
  setSortColumn,
  setSectorGrouping,
  isSectorGroupingEnabled,
  getSortConfig,
  type WatchlistQuote,
} from "./ui/watchlist";
import { createCardLifecycleStore, loadCard, type CardContext } from "./cards/registry";
import { mountWithBoundary } from "./ui/error-boundary";
import { showToast } from "./ui/toast";
import { openPalette, isPaletteOpen } from "./ui/palette-overlay";
import type { PaletteCommand } from "./ui/command-palette";
import { fetchAllTickers, fetchTickerData, type TickerData } from "./core/data-service";
import { selectedTickerStore, tickerDataStore } from "./core/app-store";
import { ensureTemporal } from "./core/temporal-init";

import { TieredCache } from "./core/tiered-cache";
import { createStoragePressureMonitor, requestPersistentStorage } from "./core/storage-pressure";
import { setScreenerData } from "./cards/screener-data";
import { setBreadthData } from "./cards/market-breadth-data";
import { computeRsiSeries } from "./domain/rsi-calculator";
import { computeSma } from "./domain/sma-calculator";
import type { ConsensusResult } from "./types";
import type { InstrumentType } from "./types/domain";
import type { ScreenerInput } from "./cards/screener";
import { buildShareUrl, readShareUrl, encodeWatchlistUrl } from "./core/share-state";
import type { ShareState } from "./core/share-state";
import { onUrlStateChange, readCurrentUrlState } from "./core/url-state";
import {
  mountInstrumentFilterBar,
  applyInstrumentFilter,
  getInstrumentFilter,
} from "./ui/instrument-filter";
import { bindSortableTable } from "./ui/sortable";
import { loadPersistedPalette, applyPalette, VALID_PALETTES } from "./ui/palette-switcher";
import { attachScrollProgress } from "./ui/scroll-driven";
import { recordAdd, recordRemove } from "./core/watchlist-history";
import { toggleTickerSelection } from "./core/ticker-selection";
import { restoreSessionState, saveSessionState } from "./core/session-state";
import { watchlistStore } from "./core/watchlist-store";
import { estimateLocalStorageUsage, updateStorageSize } from "./core/cache-stats";
import { getPerfMetrics, initPerfObserver, recordPerformanceTrace } from "./core/perf-metrics";
import { getPwaInstallManager } from "./ui/pwa-install";
import { createOnboardingTour, DEFAULT_TOUR_STEPS } from "./ui/onboarding-tour";
import { initOfflineIndicator } from "./ui/offline-indicator";
import { initCardCollapse } from "./ui/card-collapse";
import { applyAllCardWidths } from "./ui/card-width";
import { initDashboardStats } from "./ui/dashboard-stats";
import { initTelemetry, getTelemetry } from "./core/telemetry";
import { initTelemetryPreference } from "./core/telemetry-preference";
import { initPlausible } from "./core/plausible";
import { createStreamManager, getStoredFinnhubKey } from "./core/finnhub-stream-manager";
import { searchTickers } from "./providers";
import { recordTickerView } from "./core/recent-tickers";
import { isSupportedSymbol } from "./domain/ticker-catalog";
import { createAutocomplete } from "./ui/ticker-autocomplete";
import { bindHoverZoom, setHoverQuotes } from "./ui/watchlist-hover-zoom";
import { waitForFont } from "./core/font-loader";
import { linkPrefetchFallback } from "./core/speculation-rules";
import { evaluateAlertRules } from "./core/alert-rules-evaluator";
import { checkWhatsNew } from "./core/whats-new";
import { openShortcutsDialog } from "./ui/shortcuts-dialog";
import { updateFreshnessIndicator } from "./ui/freshness-indicator";
import { initSidebarToggle } from "./ui/sidebar";
import { initCardPrefetchOnIntent } from "./ui/card-prefetch";
import { restoreActiveLayoutPreset } from "./ui/layout-presets";
import { applyEnhancedFocus } from "./ui/a11y-aaa";
import { onSwipe } from "./ui/mobile-ux";
import {
  bindTickerContextMenu,
  initTickerContextMenu,
  registerTickerActions,
} from "./ui/ticker-context-menu";
import { copyToClipboard } from "./ui/clipboard";
import "./ui/chart-frame";
import "./ui/empty-state";
import "./ui/filter-bar";
import "./ui/stat-grid";

const cardHandles = createCardLifecycleStore();
const cardContainers: Partial<Record<RouteName, string>> = {
  consensus: "consensus-container",
  chart: "chart-container",
  alerts: "alerts-container",
  heatmap: "heatmap-container",
  screener: "screener-container",
  "provider-health": "provider-health-container",
  portfolio: "portfolio-container",
  risk: "risk-container",
  backtest: "backtest-container",
  "strategy-comparison": "strategy-comparison-container",
  "consensus-timeline": "consensus-timeline-container",
  "signal-dsl": "signal-dsl-container",
  "multi-chart": "multi-chart-container",
  correlation: "correlation-container",
  "market-breadth": "market-breadth-container",
  "earnings-calendar": "earnings-calendar-container",
  "macro-dashboard": "macro-dashboard-container",
  "sector-rotation": "sector-rotation-container",
  "relative-strength": "relative-strength-container",
  seasonality: "seasonality-container",
  comparison: "comparison-container",
  rebalance: "rebalance-container",
  "news-feed": "news-feed-container",
  settings: "settings-container",
};

async function activateCard(
  route: RouteName,
  params: Readonly<Record<string, string>>,
): Promise<void> {
  const containerId = cardContainers[route];
  if (!containerId) return;
  const el = document.getElementById(containerId);
  if (!el) return;

  // Inject selected ticker as symbol fallback when not already in route params
  const enrichedParams: Record<string, string> = { ...params };
  if (!enrichedParams["symbol"]) {
    const selected = selectedTickerStore.peek();
    if (selected) enrichedParams["symbol"] = selected;
  }

  const ctx: CardContext = { route, params: enrichedParams };
  const existing = cardHandles.get(route);
  if (existing) {
    existing.update?.(ctx);
    return;
  }
  const cardLoadStartedAt = performance.now();
  const handle = await mountWithBoundary(el, ctx, () => loadCard(route), {
    onError: (err) => console.error("Card error:", route, err),
  });
  recordPerformanceTrace("cardLoadMs", performance.now() - cardLoadStartedAt);
  cardHandles.set(route, handle);
  applyAllCardWidths();
}

function main(): void {
  window.__crosstidePerformanceMetrics = getPerfMetrics;
  let config = loadConfig();
  let tickerDataCache = new Map<string, TickerData>();
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  const savedSession = restoreSessionState();
  if (savedSession?.selectedTicker) selectedTickerStore.set(savedSession.selectedTicker);
  const sharedUrlState = readCurrentUrlState();
  if (sharedUrlState?.symbol) selectedTickerStore.set(sharedUrlState.symbol);
  if (
    !sharedUrlState &&
    savedSession &&
    isValidRouteName(savedSession.route) &&
    window.location.pathname.endsWith("/")
  ) {
    navigateToPath(savedSession.route, {}, { replace: true });
  }
  for (const entry of config.watchlist) watchlistStore.actions.addTicker(entry.ticker);
  const stopPerfObserver = initPerfObserver();

  // H3: eager-prefetch card chunks on hover/focus intent.
  initCardPrefetchOnIntent();

  // G16: expose font readiness for consumers that need to defer measurement.
  void waitForFont("Inter Variable").then((loaded) => {
    document.documentElement.dataset["fontReady"] = String(loaded);
  });

  // H3: prefetch route documents where the browser supports the API.
  // Speculation Rules only ships an *inline* `<script type="speculationrules">`
  // form in current browsers — external `src` (required to stay CSP-compliant
  // without 'unsafe-inline', since rule content is built from live DOM state
  // and can't be pinned to a static hash) reports "External speculation rules
  // are not yet supported." `injectSpeculationRules` still implements the
  // spec-correct, CSP-safe blob: URL approach for when that lands, but until
  // then every browser falls back to the universally-supported, CSP-neutral
  // `<link rel="prefetch">` path.
  const routeHrefs = [...document.querySelectorAll<HTMLAnchorElement>("a[data-route]")]
    .map((link) => link.getAttribute("href"))
    .filter((href): href is string => href !== null && href.length > 0);
  if (routeHrefs.length > 0) {
    linkPrefetchFallback(routeHrefs);
  }

  // ── B11: Cross-tab BroadcastChannel sync ──────────────────────────────────
  const crossTabSync = createCrossTabSync();
  const crossTabShare = createCrossTabShareSync();
  const removeUrlStateListener = onUrlStateChange((state) => {
    selectedTickerStore.set(state?.symbol ?? "");
  });
  const removeShareStateListener = crossTabShare.onShareState((state) => {
    if (state.symbol) selectedTickerStore.set(state.symbol);
    if (state.card && typeof state.card === "string") {
      const route = state.card as RouteName;
      navigateTo(route);
    }
  });

  /** Save config locally + broadcast to other open tabs + resync stream subscriptions. */
  function saveAndBroadcast(cfg: typeof config): void {
    saveConfig(cfg);
    crossTabSync.broadcastConfig(cfg);
    if (streamManager.isActive()) {
      streamManager.setTickers(cfg.watchlist.map((e) => e.ticker));
    }
  }

  // When another tab changes config, apply it here and re-render watchlist
  crossTabSync.onConfigChange((raw) => {
    if (!raw || typeof raw !== "object") return;
    config = raw as typeof config;
    const filteredCfg = {
      ...config,
      watchlist: applyInstrumentFilter(config.watchlist, getInstrumentFilter()),
    };
    refreshWatchlist(filteredCfg, buildQuotesMap());
  });

  // ── Helper: convert TickerData cache → the quotes map renderWatchlist expects ──
  function buildQuotesMap(): Map<string, WatchlistQuote> {
    const m = new Map<string, WatchlistQuote>();
    // Build a lookup of persisted names from config so they appear before first fetch.
    const persistedNames = new Map<string, string>(
      config.watchlist.filter((e) => e.name).map((e) => [e.ticker, e.name!]),
    );
    for (const [t, data] of tickerDataCache) {
      const resolvedName = data.name ?? persistedNames.get(t);
      m.set(t, {
        ticker: data.ticker,
        price: data.price,
        change: data.change,
        changePercent: data.changePercent,
        volume: data.volume,
        avgVolume: data.avgVolume,
        high52w: data.high52w,
        low52w: data.low52w,
        closes30d: data.closes30d,
        consensus: data.consensus,
        ...(data.instrumentType !== undefined && { instrumentType: data.instrumentType }),
        ...(data.sector !== undefined && { sector: data.sector }),
        ...(resolvedName !== undefined && { name: resolvedName }),
      });
    }
    return m;
  }

  /** Render watchlist + wire keyboard sort activation + aria-sort for accessibility (B14). */
  function refreshWatchlist(cfg: typeof config, quotes: Map<string, WatchlistQuote>): void {
    renderWatchlistCore(cfg, quotes);
    setHoverQuotes(quotes); // L11: keep hover-zoom popup up-to-date
    const thead = document.getElementById("watchlist-head");
    const liveRegion = document.getElementById("sort-live");
    const sortCol = (col: "ticker" | "price" | "change" | "consensus" | "volume"): void => {
      setSortColumn(col);
      const filteredCfg = {
        ...cfg,
        watchlist: applyInstrumentFilter(cfg.watchlist, getInstrumentFilter()),
      };
      refreshWatchlist(filteredCfg, buildQuotesMap());
    };
    const getAria = (col: string): string => {
      const s = getSortConfig();
      if (s.column !== col) return "none";
      return s.direction === "asc" ? "ascending" : "descending";
    };
    bindSortableTable(thead, sortCol, liveRegion, getAria);
  }

  function updateStatus(text: string): void {
    const el = document.getElementById("sync-status");
    if (el) el.textContent = text;
  }

  // The Settings card (`settings-card.ts`) saves config changes directly via
  // `saveConfig()`, bypassing this closure's local `config` copy. It dispatches
  // this event after any mutation so the in-memory copy — and anything that
  // reads it later, like the watchlist render and the refresh-interval timer —
  // stays correct instead of silently reverting on the next scheduled refresh.
  window.addEventListener("crosstide:config-changed", () => {
    config = loadConfig();
    refreshWatchlist(config, buildQuotesMap());
  });

  async function refreshData(): Promise<void> {
    const tickers = config.watchlist.map((e) => e.ticker);
    if (tickers.length === 0) {
      tickerDataCache.clear();
      tickerDataStore.set(new Map());
      refreshWatchlist(config, new Map());
      updateStatus("Ready");
      return;
    }

    updateStatus(`Fetching ${tickers.length} ticker(s)…`);

    const results = await fetchAllTickers(
      tickers,
      (done, total) => {
        updateStatus(`Loading ${done}/${total}…`);
      },
      undefined,
      config.methodWeights,
    );

    tickerDataCache = results;
    tickerDataStore.set(results);
    updateStorageSize(estimateLocalStorageUsage().bytes + JSON.stringify(results).length * 2);

    // G19: Persist company names returned by the data service into WatchlistEntry.
    // Build a map of ticker → name from successful fetches only.
    const nameMap = new Map<string, string>();
    for (const [t, data] of results) {
      if (data.name && !data.error) nameMap.set(t, data.name);
    }
    if (nameMap.size > 0) {
      const updated = updateWatchlistNames(config, nameMap);
      if (updated !== config) {
        config = updated;
        saveAndBroadcast(config);
      }
    }

    // Persist instrument type classifications so filters work after reload.
    const typeMap = new Map<string, InstrumentType>();
    for (const [t, data] of results) {
      if (data.instrumentType && !data.error) typeMap.set(t, data.instrumentType);
    }
    if (typeMap.size > 0) {
      const updated = updateWatchlistInstrumentTypes(config, typeMap);
      if (updated !== config) {
        config = updated;
        saveAndBroadcast(config);
      }
    }

    // Apply instrument filter before rendering
    const filteredConfig = {
      ...config,
      watchlist: applyInstrumentFilter(config.watchlist, getInstrumentFilter()),
    };
    refreshWatchlist(filteredConfig, buildQuotesMap());

    // Update screener with live data derived from candles
    const screenerInputs: ScreenerInput[] = [];
    for (const [ticker, data] of results) {
      if (data.error || data.candles.length < 20) continue;
      const rsiSeries = computeRsiSeries(data.candles, 14);
      const lastRsi = rsiSeries.length > 0 ? rsiSeries[rsiSeries.length - 1]!.value : null;
      const volumeRatio = data.avgVolume > 0 ? data.volume / data.avgVolume : 0;
      const sma50 = computeSma(data.candles, 50);
      const sma200 = computeSma(data.candles, 200);
      const smaValues = new Map<number, number | null>([
        [50, sma50],
        [200, sma200],
      ]);
      screenerInputs.push({
        ticker,
        price: data.price,
        consensus: data.consensus?.direction ?? "NEUTRAL",
        rsi: lastRsi,
        volumeRatio,
        smaValues,
        pe: null,
        marketCap: null,
        dividendYield: null,
        sector: data.sector ?? null,
      });
    }
    setScreenerData(screenerInputs);

    // G23: populate market-breadth bridge (uses already-computed sma50/200)
    const breadthInputs = screenerInputs.map((si) => ({
      ticker: si.ticker,
      price: si.price,
      changePercent: results.get(si.ticker)?.changePercent ?? 0,
      consensus: si.consensus,
      aboveSma50:
        si.smaValues.get(50) !== null && si.smaValues.get(50) !== undefined
          ? (results.get(si.ticker)?.price ?? 0) > si.smaValues.get(50)!
          : null,
      aboveSma200:
        si.smaValues.get(200) !== null && si.smaValues.get(200) !== undefined
          ? (results.get(si.ticker)?.price ?? 0) > si.smaValues.get(200)!
          : null,
    }));
    setBreadthData(breadthInputs);

    // L3: Evaluate multi-condition alert rules against latest signals
    const consensusMap = new Map<string, ConsensusResult>();
    for (const [t, data] of results) {
      if (data.consensus && !data.error) consensusMap.set(t, data.consensus);
    }
    evaluateAlertRules(consensusMap);

    const errors = [...results.values()].filter((d) => d.error);
    if (errors.length > 0 && errors.length < tickers.length) {
      updateStatus(`Updated (${errors.length} failed)`);
    } else if (errors.length === tickers.length) {
      updateStatus("All fetches failed — check network");
      showToast({
        message:
          "Could not fetch market data. If your network requires a proxy, configure it for your browser or development environment. Check the browser console (F12) for details.",
        type: "error",
        durationMs: 12000,
      });
    } else {
      updateStatus(`Updated ${new Date().toLocaleTimeString()}`);
    }

    updateFreshnessIndicator();
  }

  function scheduleRefresh(): void {
    if (refreshTimer) clearTimeout(refreshTimer);
    const interval = config.refreshIntervalMs ?? 300_000; // default 5 min
    refreshTimer = setTimeout(() => void refreshData(), interval);
  }

  // Initialize UI
  initLocale(); // D7: apply persisted locale & <html dir>
  initSidebarToggle();

  // P10: honour the OS colour scheme until the user picks a theme in Settings.
  // `config.theme` defaults to "dark", so passing it unconditionally made
  // `detectPreferredTheme()` unreachable and pinned every first visit to dark.
  // A returning visitor who chose a theme before the override key existed is
  // migrated here so their choice is not silently replaced by the OS value.
  if (hasStoredConfig() && getThemeOverride() === null) setThemeOverride(config.theme);
  initTheme(getThemeOverride() ?? detectPreferredTheme());
  initAutoThemeSync(); // re-applies on OS change while no override is set

  initEnhancedContrast(); // Q6: restore [data-contrast="aaa"] from localStorage
  loadPersistedPalette(); // C2: restore color-blind palette from localStorage
  restoreActiveLayoutPreset();
  const removeEnhancedFocus = applyEnhancedFocus();
  const removeMobileSwipe = onSwipe(document.body, (event) => {
    if (event.direction !== "right") return;
    const sidebarToggle = document.getElementById("sidebar-toggle");
    if (window.matchMedia("(max-width: 767px)").matches && sidebarToggle) {
      sidebarToggle.click();
    }
  });

  // Subscribe BEFORE initRouter(): it dispatches the initial route
  // synchronously, and a handler added afterwards would miss it — leaving a
  // deep-linked card (e.g. /seasonality) mounted-less and its view blank.
  let currentRoute: RouteName = "watchlist";
  onRouteChange((route, info) => {
    currentRoute = route;
    cardHandles.disposeInactive(route);
    saveSessionState({
      route,
      selectedTicker: selectedTickerStore.peek() ?? "",
      scrollY: window.scrollY,
    });
    void activateCard(route, info?.params ?? {});
    // A17: track route navigation as a pageview
    getTelemetry()?.pageview(window.location.pathname);
  });

  initRouter();
  window.addEventListener("pagehide", () => cardHandles.disposeAll(), { once: true });
  window.addEventListener("pagehide", stopPerfObserver, { once: true });
  window.addEventListener("pagehide", removeEnhancedFocus, { once: true });
  window.addEventListener("pagehide", removeMobileSwipe, { once: true });
  window.addEventListener("pagehide", removeUrlStateListener.remove, { once: true });
  initOfflineIndicator();
  initCardCollapse();
  initDashboardStats();
  const appMain = document.getElementById("app-main");
  const removeScrollProgress = appMain
    ? attachScrollProgress(appMain, (progress) => {
        appMain.style.setProperty("--scroll-progress", String(progress));
      })
    : (): void => undefined;
  window.addEventListener("pagehide", removeScrollProgress, { once: true });
  initPlausible(); // R14: privacy-respecting Plausible analytics
  checkWhatsNew();
  const cleanupTickerContextMenu = initTickerContextMenu();
  registerTickerActions([
    {
      id: "chart",
      label: "View chart",
      handler: (ticker): void => navigateToPath("chart", { symbol: ticker }),
    },
    {
      id: "copy",
      label: "Copy ticker",
      handler: (ticker): void => {
        void copyToClipboard(ticker).then((result) => {
          if (result.ok) showToast({ message: `${ticker} copied to clipboard`, type: "success" });
        });
      },
    },
  ]);
  const watchlistBody = document.getElementById("watchlist-body");
  const removeTickerContextMenuBinding = watchlistBody
    ? bindTickerContextMenu(watchlistBody)
    : (): void => undefined;
  window.addEventListener("pagehide", cleanupTickerContextMenu, { once: true });
  window.addEventListener("pagehide", removeTickerContextMenuBinding, { once: true });
  window.addEventListener("pagehide", removeShareStateListener, { once: true });
  window.addEventListener("pagehide", () => crossTabShare.destroy(), { once: true });
  refreshWatchlist(config, new Map());

  // Mount instrument filter bar (B12)
  mountInstrumentFilterBar(() => {
    const filteredConfig = {
      ...config,
      watchlist: applyInstrumentFilter(config.watchlist, getInstrumentFilter()),
    };
    refreshWatchlist(filteredConfig, buildQuotesMap());
  });

  // ── URL share-state: restore on startup ───────────────────────────────
  const startupState = readShareUrl(window.location.href);
  let activeShareRoute: RouteName | undefined;
  if (startupState?.card && typeof startupState.card === "string") {
    // Navigate to shared card if it's a valid route
    const candidateRoute = startupState.card as RouteName;
    activeShareRoute = candidateRoute;
  }
  // D5: restore shared watchlist from URL (only when local watchlist is empty)
  if (
    Array.isArray(startupState?.watchlist) &&
    startupState.watchlist.length > 0 &&
    config.watchlist.length === 0
  ) {
    for (const ticker of startupState.watchlist) {
      if (typeof ticker === "string" && ticker) config = addTicker(config, ticker);
    }
    saveAndBroadcast(config);
    refreshWatchlist(config, new Map());
    showToast({
      message: `Loaded ${config.watchlist.length} tickers from shared link`,
      type: "success",
    });
  }

  // Fetch live data on startup — moved to bottom of main() so streaming starts after data loads

  // Navigate to shared route after router is up (startup share-state)
  if (activeShareRoute) {
    navigateTo(activeShareRoute);
  }

  // Version display
  const versionEl = document.getElementById("app-version");
  if (versionEl) {
    versionEl.textContent = `v${__APP_VERSION__}`;
  }

  // Market hours indicator
  const footer = document.getElementById("app-footer");
  if (footer) {
    const marketEl = document.createElement("span");
    marketEl.id = "market-status-indicator";
    footer.appendChild(marketEl);
    void import("./ui/market-indicator").then(({ mountMarketIndicator }) => {
      mountMarketIndicator(marketEl);
    });
  }

  // Add ticker via autocomplete widget (enhanced search-as-you-type)
  const addInput = document.getElementById("add-ticker") as HTMLInputElement | null;
  let autocompleteHandle: ReturnType<typeof createAutocomplete> | null = null;

  function handleAddTicker(ticker: string): void {
    const t = ticker.trim().toUpperCase();
    if (!t) return;
    if (!isSupportedSymbol(t)) {
      showToast({ message: `Invalid ticker: ${t}`, type: "error" });
      return;
    }
    recordTickerView(t);
    if (config.watchlist.some((e) => e.ticker === t)) {
      showToast({ message: `${t} already in watchlist`, type: "warning" });
      return;
    }
    config = addTicker(config, t);
    watchlistStore.actions.addTicker(t);
    recordAdd(t);
    saveAndBroadcast(config);
    refreshWatchlist(config, new Map());
    showToast({ message: `Added ${t} — fetching data…`, type: "success" });
    maybeRequestPersist();
    void fetchTickerData(t).then((data) => {
      tickerDataCache.set(t, data);
      tickerDataStore.set(new Map(tickerDataCache));
      void refreshData();
    });
  }

  if (addInput) {
    autocompleteHandle = createAutocomplete({
      onSearch: (query) => searchTickers(query),
      onSelect: handleAddTicker,
      placeholder: "Search ticker (e.g. AAPL)…",
      inputId: "add-ticker",
    });
    addInput.replaceWith(autocompleteHandle.element);
  }

  // D5: Share watchlist button — encode tickers into a deep-link URL
  const shareWlBtn = document.getElementById("btn-share-watchlist") as HTMLButtonElement | null;
  shareWlBtn?.addEventListener("click", () => {
    const tickers = config.watchlist.map((e) => e.ticker);
    const url = encodeWatchlistUrl(tickers, window.location.href);
    void navigator.clipboard
      .writeText(url)
      .then(() => {
        showToast({ message: "Watchlist link copied to clipboard!", type: "success" });
      })
      .catch(() => {
        showToast({ message: `Watchlist link: ${url}`, type: "info", durationMs: 0 });
      });
  });

  // Remove ticker via event delegation
  const tbody = document.getElementById("watchlist-body");
  tbody?.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    if (target.dataset["action"] === "select") {
      const ticker = target.dataset["ticker"];
      if (ticker) {
        toggleTickerSelection(ticker);
        refreshWatchlist(config, buildQuotesMap());
      }
      return;
    }
    if (target.dataset["action"] === "remove") {
      const ticker = target.dataset["ticker"];
      if (ticker) {
        config = removeTicker(config, ticker);
        watchlistStore.actions.removeTicker(ticker);
        recordRemove(ticker);
        saveAndBroadcast(config);
        refreshWatchlist(config, new Map());
        showToast({ message: `Removed ${ticker}`, type: "info" });
      }
      return;
    }
    // Clicking a row (not the remove button) selects ticker and opens chart
    const row = target.closest<HTMLElement>("tr[data-ticker]");
    if (row) {
      const ticker = row.dataset["ticker"];
      if (ticker) {
        selectedTickerStore.set(ticker);
        navigateToPath("chart", { symbol: ticker });
      }
    }
  });

  // ── L11: Hover zoom on watchlist rows ──────────────────────────────────────
  if (tbody) {
    bindHoverZoom(tbody);
  }

  // Column sorting via header click
  const watchlistTable = document.getElementById("watchlist-table");
  watchlistTable?.addEventListener("click", (e) => {
    const th = (e.target as HTMLElement).closest<HTMLElement>("[data-sort]");
    if (!th) return;
    const col = th.dataset["sort"] as "ticker" | "price" | "change" | "consensus" | "volume";
    setSortColumn(col);
    const sortedConfig = {
      ...config,
      watchlist: applyInstrumentFilter(config.watchlist, getInstrumentFilter()),
    };
    refreshWatchlist(sortedConfig, buildQuotesMap());
    const s = getSortConfig();
    const liveRegion = document.getElementById("sort-live");
    if (liveRegion) {
      liveRegion.textContent = `Sorted by ${col} ${s.direction === "asc" ? "ascending" : "descending"}`;
    }
  });

  // ── A11: Drag-reorder watchlist rows ──────────────────────────────────────
  if (tbody) {
    let dragFromIndex: number | null = null;

    tbody.addEventListener("dragstart", (e) => {
      const row = (e.target as HTMLElement).closest<HTMLElement>("tr[data-ticker]");
      if (!row) return;
      dragFromIndex = [...tbody.children].indexOf(row);
      row.classList.add("dragging");
      e.dataTransfer?.setData("text/plain", row.dataset["ticker"] ?? "");
    });

    tbody.addEventListener("dragover", (e) => {
      e.preventDefault();
      const row = (e.target as HTMLElement).closest<HTMLElement>("tr[data-ticker]");
      if (row) row.classList.add("drag-over");
    });

    tbody.addEventListener("dragleave", (e) => {
      const row = (e.target as HTMLElement).closest<HTMLElement>("tr[data-ticker]");
      if (row) row.classList.remove("drag-over");
    });

    tbody.addEventListener("drop", (e) => {
      e.preventDefault();
      const row = (e.target as HTMLElement).closest<HTMLElement>("tr[data-ticker]");
      if (!row || dragFromIndex === null) return;
      row.classList.remove("drag-over");
      const toIndex = [...tbody.children].indexOf(row);
      if (toIndex !== dragFromIndex) {
        config = reorderWatchlist(config, dragFromIndex, toIndex);
        saveAndBroadcast(config);
        refreshWatchlist(config, buildQuotesMap());
      }
      dragFromIndex = null;
    });

    tbody.addEventListener("dragend", () => {
      dragFromIndex = null;
      tbody.querySelectorAll(".dragging").forEach((el) => el.classList.remove("dragging"));
    });
  }

  // Theme, palette, export/import/clear, and full-data-export controls are
  // owned by the Settings card (`src/cards/settings-card.ts`) — it dispatches
  // "crosstide:config-changed" after any mutation so this closure's `config`
  // and the rendered watchlist stay in sync (see the listener below).

  // --- Command Palette & Keyboard Shortcuts ---
  const shortcuts = createShortcutManager();
  const paletteCommands: PaletteCommand[] = [
    {
      id: "nav-watchlist",
      label: "Go to Watchlist",
      hint: "G W",
      section: "Navigation",
      run: () => navigateTo("watchlist"),
    },
    {
      id: "nav-consensus",
      label: "Go to Consensus",
      hint: "G C",
      section: "Navigation",
      run: () => navigateTo("consensus"),
    },
    {
      id: "nav-chart",
      label: "Go to Chart",
      hint: "G H",
      section: "Navigation",
      run: () => navigateTo("chart"),
    },
    {
      id: "nav-alerts",
      label: "Go to Alerts",
      hint: "G A",
      section: "Navigation",
      run: () => navigateTo("alerts"),
    },
    {
      id: "nav-heatmap",
      label: "Go to Heatmap",
      hint: "G M",
      section: "Navigation",
      run: () => navigateTo("heatmap"),
    },
    {
      id: "nav-screener",
      label: "Go to Screener",
      hint: "G R",
      section: "Navigation",
      run: () => navigateTo("screener"),
    },
    {
      id: "nav-settings",
      label: "Go to Settings",
      hint: "G S",
      section: "Navigation",
      run: () => navigateTo("settings"),
    },
    {
      id: "nav-provider-health",
      label: "Go to Provider Health",
      hint: "G P",
      section: "Navigation",
      run: () => navigateTo("provider-health"),
    },
    {
      id: "nav-portfolio",
      label: "Go to Portfolio",
      section: "Navigation",
      run: () => navigateTo("portfolio"),
    },
    {
      id: "nav-risk",
      label: "Go to Risk Metrics",
      section: "Navigation",
      run: () => navigateTo("risk"),
    },
    {
      id: "nav-backtest",
      label: "Go to Backtest",
      section: "Navigation",
      run: () => navigateTo("backtest"),
    },
    {
      id: "nav-consensus-timeline",
      label: "Go to Consensus Timeline",
      section: "Navigation",
      run: () => navigateTo("consensus-timeline"),
    },
    {
      id: "add-ticker",
      label: "Add Ticker",
      hint: "A",
      section: "Actions",
      run: () => autocompleteHandle?.focus(),
    },
    {
      id: "refresh-data",
      label: "Refresh Data",
      hint: "R",
      section: "Actions",
      run: () => void refreshData(),
    },
    {
      id: "search-focus",
      label: "Focus Search",
      hint: "/",
      section: "Actions",
      run: () => autocompleteHandle?.focus(),
    },
    {
      id: "copy-share-link",
      label: "Copy share link for current view",
      hint: "Shift+S",
      section: "Actions",
      run: (): void => {
        const symbol =
          getCurrentRouteInfo().params["symbol"] || selectedTickerStore.peek() || undefined;
        const shareUrl = buildShareUrl(window.location.pathname, {
          card: currentRoute,
          ...(symbol ? { symbol } : {}),
        });
        const fullUrl = window.location.origin + shareUrl;
        void navigator.clipboard
          .writeText(fullUrl)
          .then(() => {
            showToast({ message: "Share link copied to clipboard!", type: "success" });
          })
          .catch(() => {
            showToast({ message: `Share link: ${fullUrl}`, type: "info", durationMs: 0 });
          });
      },
    },
    {
      id: "share-watchlist",
      label: "Share watchlist URL",
      section: "Actions",
      run: () => shareWlBtn?.click(),
    },
    {
      id: "check-storage",
      label: "Check storage usage",
      section: "Actions",
      run: (): void => {
        void pressureMonitor.check().then((e) => {
          if (!e) {
            showToast({ message: "Storage estimate unavailable in this browser.", type: "info" });
            return;
          }
          const pct = (e.ratio * 100).toFixed(1);
          const usedMb = (e.usage / 1024 / 1024).toFixed(1);
          const quotaMb = (e.quota / 1024 / 1024).toFixed(0);
          showToast({
            message: `Storage: ${pct}% used (${usedMb} MB / ${quotaMb} MB)`,
            type: e.ratio >= 0.8 ? "warning" : "info",
          });
        });
      },
    },
    {
      id: "clear-cache",
      label: "Clear app cache",
      section: "Actions",
      run: (): void => {
        appCache.clear();
        localStorage.removeItem("crosstide-cache");
        showToast({ message: "App cache cleared.", type: "info" });
      },
    },
    {
      id: "toggle-sector-grouping",
      label: "Toggle sector grouping in watchlist",
      section: "Actions",
      run: (): void => {
        const next = !isSectorGroupingEnabled();
        setSectorGrouping(next);
        const filteredConfig = {
          ...config,
          watchlist: applyInstrumentFilter(config.watchlist, getInstrumentFilter()),
        };
        refreshWatchlist(filteredConfig, buildQuotesMap());
        showToast({ message: `Sector grouping ${next ? "enabled" : "disabled"}`, type: "info" });
      },
    },
    // ── C2: Color-blind / high-contrast palette commands ──────────────────
    ...VALID_PALETTES.map((pal) => ({
      id: `set-palette-${pal}`,
      label: `Color palette: ${pal.charAt(0).toUpperCase() + pal.slice(1).replace("-", " ")}`,
      section: "Appearance",
      run: (): void => {
        applyPalette(pal);
        const select = document.getElementById("palette-select") as HTMLSelectElement | null;
        if (select) select.value = pal;
        showToast({ message: `Palette: ${pal}`, type: "info" });
      },
    })),
  ];

  // Ctrl+K / Cmd+K → open palette
  shortcuts.register({
    action: "open-palette",
    key: "k",
    ctrl: true,
    description: "Open command palette",
    handler: () => openPalette(paletteCommands),
  });

  // "/" → focus search (when palette not open)
  shortcuts.register({
    action: "focus-search",
    key: "/",
    description: "Focus ticker search",
    handler: () => autocompleteHandle?.focus(),
  });

  // "r" → refresh data
  shortcuts.register({
    action: "refresh-data",
    key: "r",
    description: "Refresh data",
    handler: () => void refreshData(),
  });

  // Shift+S → copy share link
  shortcuts.register({
    action: "copy-share-link",
    key: "s",
    shift: true,
    description: "Copy share link for current view",
    handler: () => {
      const symbol =
        getCurrentRouteInfo().params["symbol"] || selectedTickerStore.peek() || undefined;
      const shareState: ShareState = { card: currentRoute, ...(symbol ? { symbol } : {}) };
      const shareUrl = buildShareUrl(window.location.pathname, shareState);
      crossTabShare.broadcastShareState(shareState);
      const fullUrl = window.location.origin + shareUrl;
      void navigator.clipboard
        .writeText(fullUrl)
        .then(() => {
          showToast({ message: "Share link copied to clipboard!", type: "success" });
        })
        .catch(() => {
          showToast({ message: `Share link: ${fullUrl}`, type: "info", durationMs: 0 });
        });
    },
  });

  // "?" → show shortcuts help
  shortcuts.register({
    action: "show-shortcuts",
    key: "?",
    shift: true,
    description: "Show keyboard shortcuts",
    handler: () => {
      openShortcutsDialog();
    },
  });

  // Escape → close palette if open, otherwise blur the focused input
  shortcuts.register({
    action: "close-overlay",
    key: "Escape",
    description: "Close palette or unfocus input",
    handler: () => {
      if (isPaletteOpen()) {
        /* handled by palette input */
      } else if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    },
  });

  void shortcuts; // retain reference

  // --- Storage Pressure Monitor ---
  const appCache = new TieredCache();
  let storagePersistRequested = false;
  const pressureMonitor = createStoragePressureMonitor({
    threshold: 0.8,
    intervalMs: 60_000,
    onPressure: (estimate) => {
      const pct = (estimate.ratio * 100).toFixed(0);
      const evicted = appCache.evictOldest(20);
      showToast({
        message: `Storage ${pct}% full — freed ${evicted} cache entr${evicted === 1 ? "y" : "ies"}. Consider clearing old data.`,
        type: "warning",
        durationMs: 8000,
      });
      console.warn(`[storage-pressure] ${pct}% used — evicted ${evicted} cache entries`);
    },
  });
  pressureMonitor.start();
  void appCache; // retain reference

  // ── C8: PWA install prompt ─────────────────────────────────────────────────
  // The install/dismiss UI itself lives in the Settings card (`settings.ts`),
  // which shares this same manager instance. main() only needs the app-wide
  // "installed" toast, which should fire regardless of the active route.
  getPwaInstallManager().onInstalled(() => {
    showToast({ message: "CrossTide installed as an app!", type: "success" });
  });

  // ── C9: Onboarding tour — show on first visit ──────────────────────────────
  const tour = createOnboardingTour(DEFAULT_TOUR_STEPS);
  // Delay slightly so DOM is settled and styles are applied
  setTimeout(() => tour.start(), 800);
  // "Reset tour" palette command for testers
  void tour; // retain reference

  // Request persistent storage on first ticker add (A21)
  function maybeRequestPersist(): void {
    if (storagePersistRequested) return;
    storagePersistRequested = true;
    void requestPersistentStorage().then((granted) => {
      if (!granted) {
        // Storage not granted on first visit — this is normal
        void granted;
      }
    });
  }

  // ── B1: Finnhub WebSocket streaming ────────────────────────────────────────
  const streamManager = createStreamManager();

  /** Apply a live-price tick to the watchlist row without a full re-render. */
  streamManager.onLiveTick(({ ticker, price }) => {
    const priceCell = document.querySelector<HTMLElement>(
      `#watchlist-body tr[data-ticker="${CSS.escape(ticker)}"] .price-cell`,
    );
    if (priceCell) {
      priceCell.textContent = price.toFixed(2);
      priceCell.classList.add("live-flash");
      setTimeout(() => priceCell.classList.remove("live-flash"), 800);
    }
    // Also keep tickerDataCache in sync so next poll re-render shows live value
    const cached = tickerDataCache.get(ticker);
    if (cached) {
      tickerDataCache.set(ticker, { ...cached, price });
      tickerDataStore.set(new Map(tickerDataCache));
    }
  });

  streamManager.onStatusChange((status) => {
    const statusEl = document.getElementById("sync-status");
    if (!statusEl) return;
    if (status === "connected") {
      statusEl.textContent = "● LIVE";
      statusEl.classList.add("status-live");
    } else if (status === "connecting") {
      statusEl.textContent = "Connecting…";
      statusEl.classList.remove("status-live");
    } else if (status === "disconnected" || status === "error") {
      statusEl.textContent = "Stream disconnected";
      statusEl.classList.remove("status-live");
    } else {
      statusEl.classList.remove("status-live");
    }
  });

  /** Start or restart the stream with the current ticker list. */
  function startStreamIfKeySet(): void {
    const apiKey = getStoredFinnhubKey();
    if (!apiKey) return;
    const tickers = config.watchlist.map((e) => e.ticker);
    streamManager.start(apiKey, tickers);
  }

  // Command-palette entry for stream control
  paletteCommands.push({
    id: "toggle-live-stream",
    label: streamManager.isActive() ? "Stop real-time streaming" : "Start real-time streaming",
    section: "Actions",
    run: (): void => {
      if (streamManager.isActive()) {
        streamManager.stop();
        showToast({ message: "Real-time streaming stopped", type: "info" });
      } else {
        const key = getStoredFinnhubKey();
        if (!key) {
          showToast({
            message: "Add a Finnhub API key in Settings to enable live streaming",
            type: "warning",
          });
          navigateTo("settings");
        } else {
          startStreamIfKeySet();
          showToast({ message: "Starting live stream…", type: "info" });
        }
      }
    },
  });

  // Start streaming after initial data load if key is stored
  void refreshData().then(() => {
    scheduleRefresh();
    startStreamIfKeySet();
  });
  recordPerformanceTrace("startupMs", performance.now() - startupStartedAt);
}

// P15: Kick off polyfill loading before the app mounts.
// On Chrome 131+ the dynamic import is never fetched (native Temporal exists).
void ensureTemporal();
const startupStartedAt = performance.now();
main();

// ── A17: Telemetry — analytics + error tracking + web vitals ──────────────
// Env-gated: no-op unless VITE_PLAUSIBLE_URL / VITE_GLITCHTIP_DSN are set.
const telemetry = initTelemetry();
initTelemetryPreference(); // G05: apply a stored opt-out before the first event fires
telemetry.pageview(); // initial pageview

// Register PWA service worker.
// `/sw.js` is only emitted by the production build (see vite.config.ts's
// rollupOptions.input.sw) — the dev server (used by E2E tests too) has no
// such file, so registering there would 404 → SPA-fallback to index.html,
// producing a spurious "unsupported MIME type ('text/html')" console error.
if (import.meta.env.PROD) {
  void registerServiceWorker().then((reg) => {
    if (reg) {
      watchServiceWorkerUpdates(reg, {
        onUpdateReady: (handle) => {
          const banner = document.createElement("div");
          banner.className = "sw-update-banner";
          banner.setAttribute("role", "alert");
          banner.setAttribute("aria-live", "assertive");

          const msg = document.createElement("span");
          msg.textContent = "A new version is available.";
          banner.appendChild(msg);

          const btn = document.createElement("button");
          btn.textContent = "Refresh";
          btn.className = "sw-update-btn";
          btn.addEventListener("click", () => {
            btn.disabled = true;
            btn.textContent = "Updating...";
            activateServiceWorkerUpdate(handle, navigator.serviceWorker, () => {
              window.location.reload();
            });
          });
          banner.appendChild(btn);

          const dismiss = document.createElement("button");
          dismiss.textContent = "Later";
          dismiss.className = "sw-update-dismiss";
          dismiss.setAttribute("aria-label", "Dismiss update");
          dismiss.addEventListener("click", () => banner.remove());
          banner.appendChild(dismiss);

          document.body.appendChild(banner);
        },
      });
    }
  });
}
